import { useState, useEffect, useRef } from 'react';
import {
  Command, Send, Trash2, BrainCircuit, Search, Shield, X,
  ChevronDown, ChevronRight, CheckCircle2, Loader2
} from 'lucide-react';

/* ─── Markdown-lite renderer ─── */
const fmt = (t) => t
  .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
  .replace(/\*(.+?)\*/g, '<em class="text-slate-200">$1</em>');

const RichText = ({ text }) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**') && !line.includes(':**'))
      return <h3 key={i} className="text-[13px] font-bold text-cyan-400 mt-5 mb-1.5 uppercase tracking-wider">{line.replace(/\*\*/g, '')}</h3>;
    if (line.trim().startsWith('*   **'))
      return <div key={i} className="ml-3 mb-1.5 text-[12.5px] leading-relaxed"><span className="text-cyan-500/60 mr-1.5">▸</span><span dangerouslySetInnerHTML={{ __html: fmt(line.trim().slice(4)) }} /></div>;
    if (line.trim().match(/^\*\s/))
      return <div key={i} className="ml-3 mb-1 text-[12.5px] text-slate-400 leading-relaxed"><span className="text-slate-600 mr-1.5">•</span><span dangerouslySetInnerHTML={{ __html: fmt(line.trim().slice(2)) }} /></div>;
    if (!line.trim()) return <div key={i} className="h-1.5" />;
    return <p key={i} className="text-[12.5px] text-slate-300 leading-relaxed mb-1" dangerouslySetInnerHTML={{ __html: fmt(line) }} />;
  });
};

/* ─── Animated thinking pill ─── */
const ThinkingPill = ({ label, status }) => (
  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-mono tracking-wide transition-all duration-300 ${
    status === 'active' ? 'bg-cyan-500/10 border border-cyan-500/20 text-cyan-400' :
    status === 'done' ? 'bg-slate-800/40 border border-slate-700/30 text-slate-500' :
    'bg-slate-900/30 border border-slate-800/20 text-slate-600'
  }`}>
    {status === 'active' && (
      <div className="relative w-3 h-3 shrink-0">
        <div className="absolute inset-0 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
      </div>
    )}
    {status === 'done' && <CheckCircle2 className="w-3 h-3 text-cyan-500/50 shrink-0" />}
    <span className="truncate">{label}</span>
  </div>
);

/* ─── Message bubble ─── */
const Bubble = ({ msg }) => {
  const [expanded, setExpanded] = useState(false);

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-[80%] bg-indigo-600/15 border border-indigo-500/20 rounded-2xl rounded-tr-sm px-5 py-3 text-[13px] text-slate-200 leading-relaxed">
          {msg.text}
        </div>
      </div>
    );
  }

  // Assistant
  const hasSteps = msg.steps?.length > 0;
  const isThinking = msg.isStreaming && !msg.text;

  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-8 h-8 rounded-xl bg-[#111827] border border-slate-700/50 flex items-center justify-center shrink-0">
        {isThinking
          ? <div className="relative"><BrainCircuit className="w-4 h-4 text-cyan-400" /><div className="absolute -inset-1 rounded-full bg-cyan-400/15 animate-ping" /></div>
          : <Shield className="w-4 h-4 text-cyan-400" />
        }
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        {/* Thinking state — no text yet */}
        {isThinking && (
          <div className="space-y-2">
            <span className="text-[11px] font-mono text-cyan-400/80 tracking-widest uppercase">
              Analysing...
            </span>
            {hasSteps && (
              <div className="flex flex-wrap gap-1.5">
                {msg.steps.map((s, i) => <ThinkingPill key={i} label={s.label} status={s.active ? 'active' : s.done ? 'done' : 'pending'} />)}
              </div>
            )}
          </div>
        )}

        {/* Final response */}
        {msg.text && (
          <div className="bg-[#0B1120] border border-slate-800/50 rounded-2xl rounded-tl-sm px-5 py-4 overflow-hidden">
            {/* Collapsed reasoning steps */}
            {hasSteps && (
              <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-[9px] font-mono text-slate-600 hover:text-slate-400 transition-colors tracking-widest uppercase mb-2">
                {expanded ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                {msg.steps.length} steps
              </button>
            )}
            {expanded && (
              <div className="flex flex-wrap gap-1 mb-3 pb-3 border-b border-slate-800/40">
                {msg.steps.map((s, i) => <ThinkingPill key={i} label={s.label} status="done" />)}
              </div>
            )}
            <RichText text={msg.text} />
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main Chat (embeddable panel) ─── */
const DefenseChat = ({ onClose }) => {
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('frontier_chat') || '[]'); } catch { return []; }
  });
  const [sid, setSid] = useState(() => localStorage.getItem('frontier_sid') || null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('frontier_chat', JSON.stringify(messages));
    if (sid) localStorage.setItem('frontier_sid', sid);
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sid]);

  const clear = () => { localStorage.removeItem('frontier_chat'); localStorage.removeItem('frontier_sid'); setMessages([]); setSid(null); };

  const send = async (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q || loading) return;
    setMessages(p => [...p, { role: 'user', text: q }, { role: 'assistant', text: '', steps: [], isStreaming: true }]);
    setInput(''); setLoading(true);

    try {
      const res = await fetch('https://us-central1-golden-union-490801-d1.cloudfunctions.net/defense-tech-ai-assistant-fun', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, user_id: 'zuhaib_user', session_id: sid })
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let full = '', steps = [], buf = '';

      const update = (patch) => setMessages(p => { const u = [...p]; u[u.length - 1] = { ...u[u.length - 1], ...patch }; return u; });

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n'); buf = lines.pop() || '';

        for (const ln of lines) {
          if (!ln.trim()) continue;
          try {
            const d = JSON.parse(ln);
            if (d.session_id && !sid) setSid(d.session_id);
            const parts = d.content?.content?.parts;
            if (!parts) continue;
            for (const p of parts) {
              if (p.function_call) {
                const n = p.function_call.name || '';
                const lbl = n.includes('vertex_ai_search') ? 'Searching knowledge base...' :
                            n.includes('google_search') ? 'Searching live signals...' : `Calling ${n.split('_').pop()}...`;
                steps = steps.map(s => ({ ...s, active: false, done: true }));
                steps.push({ label: lbl, active: true, done: false });
                update({ steps: [...steps] });
              }
              if (p.function_response) {
                steps = steps.map(s => ({ ...s, active: false, done: true }));
                update({ steps: [...steps] });
              }
              if (p.text) {
                full += p.text;
                steps = steps.map(s => ({ ...s, active: false, done: true }));
                update({ text: full, steps: [...steps], isStreaming: false });
              }
            }
          } catch { /* partial */ }
        }
      }
      // flush buffer
      if (buf.trim()) try { const d = JSON.parse(buf); const t = d.content?.content?.parts?.find(p => p.text)?.text; if (t) { full += t; update({ text: full, isStreaming: false }); } } catch {}
      update({ isStreaming: false });
    } catch (err) {
      console.error(err);
      setMessages(p => { const u = [...p]; u[u.length - 1] = { ...u[u.length - 1], text: 'Signal lost. Please retry.', isStreaming: false }; return u; });
    } finally { setLoading(false); }
  };

  const suggestions = ['Shield AI latest funding', 'Anduril Lattice moat analysis', 'C-UAS market leaders 2026'];

  return (
    <div className={`flex flex-col bg-[#0a0f1a] overflow-hidden ${onClose ? 'h-full rounded-2xl border border-slate-700/40 shadow-[0_25px_80px_rgba(0,0,0,0.8)]' : 'min-h-screen'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#060913] border-b border-slate-800/60 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-[#212736] border border-slate-700/40">
            <Command className="w-3.5 h-3.5 text-indigo-400" />
          </div>
          <div>
            <span className="text-xs font-black tracking-[0.15em] uppercase text-white">Frontier</span>
            <span className="text-[8px] font-mono text-slate-600 tracking-widest uppercase ml-2">Intel Agent</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={clear} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-400 hover:bg-slate-800/50 transition-all" title="Clear">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {onClose && (
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-400 hover:bg-slate-800/50 transition-all" title="Close">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 custom-scrollbar">
        <div className={onClose ? '' : 'max-w-3xl mx-auto'}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full min-h-[40vh] text-center px-4">
            <div className="p-4 rounded-2xl bg-[#0B1120] border border-slate-800/40 mb-5">
              <BrainCircuit className="w-9 h-9 text-indigo-400/40" />
            </div>
            <h2 className="text-base font-bold text-white mb-1">Defense Intelligence Agent</h2>
            <p className="text-[12px] text-slate-500 max-w-xs leading-relaxed mb-6">
              Ask about defense tech companies, funding, M&A, or strategy.
            </p>
            <div className="flex flex-col gap-2 w-full max-w-sm">
              {suggestions.map(q => (
                <button key={q} onClick={() => { setInput(q); inputRef.current?.focus(); }}
                  className="w-full text-left px-4 py-2.5 rounded-xl bg-[#111827] border border-slate-800/40 text-[12px] text-slate-500 hover:text-cyan-400 hover:border-cyan-500/20 transition-all truncate">
                  <Search className="w-3 h-3 inline mr-2 opacity-40" />{q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} msg={m} />)}
        <div ref={endRef} />
        </div>
      </div>

      {/* Input */}
      <form onSubmit={send} className="flex items-center gap-2.5 px-4 md:px-6 py-3.5 bg-[#060913] border-t border-slate-800/60 shrink-0">
        <div className={`flex items-center gap-2.5 flex-1 ${onClose ? '' : 'max-w-3xl mx-auto w-full'}`}>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} disabled={loading}
            placeholder="Ask about defense tech, funding, or strategy..."
            className="flex-1 bg-[#0B1120] border border-slate-800/50 rounded-xl px-4 py-2.5 text-[13px] text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500/50 transition-all disabled:opacity-40" />
          <button type="submit" disabled={loading || !input.trim()}
            className="p-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-20 disabled:cursor-not-allowed text-white transition-all shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DefenseChat;
